import { useState, type ChangeEvent } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchPlayers, useSearchPlayerMatches } from "../../../hooks/queries/useSuperAdmin";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { formatDate, playerToString } from "../../../util/StringUtils";
import SetMatchResultModal from "../../matches/SetMatchResultModal";
import type { Match, Player } from "../../../types/domain";

/**
 * Find any match → set its result. Workflow: search player by name → pick a player →
 * see all their matches (any state) → click → SetMatchResultModal (reused).
 */
function MatchSection() {
  const { t } = useTranslation();
  const [playerQuery, setPlayerQuery] = useState("");
  const debouncedPlayerQuery = useDebouncedValue(playerQuery, 300);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);

  const { data: players, isFetching: playersLoading } = useSearchPlayers(
    { q: debouncedPlayerQuery, page: 0, size: 10 },
    debouncedPlayerQuery.length > 0 && selectedPlayer == null,
  );

  const { data: matches, isFetching: matchesLoading } = useSearchPlayerMatches(
    selectedPlayer?.id ?? null,
    0,
    50,
  );

  // No selected player → show player search results.
  if (selectedPlayer == null) {
    return (
      <div>
        <Form.Control
          type="text"
          placeholder={t("zakulisi__searchPlayerForMatches")}
          value={playerQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPlayerQuery(e.target.value)}
          className="mb-3"
        />
        {playersLoading && <Spinner animation="border" size="sm" className="ms-2" />}
        {players && players.content.length > 0 && (
          <Table striped bordered hover size="sm" variant="dark">
            <thead>
              <tr>
                <th style={{ width: "5rem" }}>ID</th>
                <th>{t("player")}</th>
                <th style={{ width: "8rem" }}>{t("auditLog__action")}</th>
              </tr>
            </thead>
            <tbody>
              {players.content.map((player) => (
                <tr key={player.id}>
                  <td>{player.id}</td>
                  <td>{playerToString(player)}</td>
                  <td>
                    <Button variant="outline-info" size="sm" onClick={() => setSelectedPlayer(player)}>
                      {t("zakulisi__viewMatches")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {debouncedPlayerQuery && players && players.content.length === 0 && !playersLoading && (
          <div className="text-center" style={{ color: "var(--text-muted)" }}>{t("auditLog__empty")}</div>
        )}
      </div>
    );
  }

  // Selected player → list their matches.
  return (
    <div>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <Button variant="outline-secondary" size="sm" onClick={() => setSelectedPlayer(null)}>← {t("zakulisi__back")}</Button>
        <div>
          {t("player")}: <strong>{playerToString(selectedPlayer)}</strong>
        </div>
      </div>

      {matchesLoading && <Spinner animation="border" size="sm" />}
      {matches && matches.content.length > 0 && (
        <Table striped bordered hover size="sm" variant="dark">
          <thead>
            <tr>
              <th style={{ width: "5rem" }}>ID</th>
              <th style={{ width: "7rem" }}>{t("zakulisi__date")}</th>
              <th>{t("tournamentName")}</th>
              <th>{t("player")} 1</th>
              <th>{t("player")} 2</th>
              <th style={{ width: "7rem" }}>{t("zakulisi__state")}</th>
              <th style={{ width: "6rem" }}>{t("auditLog__action")}</th>
            </tr>
          </thead>
          <tbody>
            {matches.content.map((match) => (
              <tr key={match.id}>
                <td>{match.id}</td>
                <td>{formatDate(match.createdAt)}</td>
                <td>{match.tournament.name}</td>
                <td>{playerToString(match.playerOne, false, true)}</td>
                <td>{playerToString(match.playerTwo, false, true)}</td>
                <td>{match.state}</td>
                <td>
                  <Button variant="outline-info" size="sm" onClick={() => setEditingMatch(match)}>
                    {t("zakulisi__edit")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {matches && matches.content.length === 0 && !matchesLoading && (
        <div className="text-center" style={{ color: "var(--text-muted)" }}>{t("auditLog__empty")}</div>
      )}

      {editingMatch && (
        <SetMatchResultModal
          isOpen={true}
          match={editingMatch}
          closeModal={() => setEditingMatch(null)}
        />
      )}
    </div>
  );
}

export default MatchSection;
